<script setup lang="ts">
import {computed} from 'vue';
import VChart from 'vue-echarts';
import type {EChartsOption, SeriesOption} from 'echarts';
import '@/base/echarts.js';
import {DashboardTargetRuleEnum, formatWaarde, KLEUR_LIJN, KLEUR_TARGET, puntKleuren, ReeksPunt, ReeksSerie, WaardeTypeEnum} from 'common';

const props = defineProps<{
    serie: ReeksSerie;
    type: 'bar' | 'line';
    compact?: boolean;
}>();

const isPercentage = computed<boolean>(() => props.serie.waardeType === WaardeTypeEnum.percentage);

// a vaste-waarde target is drawn as a line; the other rules compare against earlier points, not a fixed value
const targetWaarde = computed<number | undefined>(() => props.serie.target?.rule === DashboardTargetRuleEnum.vasteWaarde ? props.serie.target.value : undefined);

function format(waarde: number): string {
    return formatWaarde(waarde, props.serie.waardeType);
}

// the first measurement is the baseline of every dashboard chart
function markLines(): object[] {
    const eerste: ReeksPunt | undefined = props.serie.punten[0];
    const lines: object[]               = [];
    if (eerste) {
        lines.push({yAxis: eerste.waarde, lineStyle: {type: 'dotted', color: KLEUR_LIJN}, label: {formatter: `nulmeting ${eerste.kwartaal}: ${format(eerste.waarde)}`, position: 'insideEndTop'}});
    }
    if (targetWaarde.value !== undefined) {
        lines.push({yAxis: targetWaarde.value, lineStyle: {type: 'dotted', color: KLEUR_TARGET}, label: {formatter: `target: ${format(targetWaarde.value)}`, position: 'insideEndBottom'}});
    }
    return lines;
}

// widen the auto-computed range so a vaste-waarde target and the baseline are never clipped off a line chart
function yMin(v: {min: number}): number {
    const eerste: number | undefined = props.serie.punten[0]?.waarde;
    return Math.min(v.min, eerste ?? v.min, targetWaarde.value ?? v.min);
}

function yMax(v: {max: number}): number {
    const eerste: number | undefined = props.serie.punten[0]?.waarde;
    return Math.max(v.max, eerste ?? v.max, targetWaarde.value ?? v.max);
}

// one bar or line series; the cast is needed because SeriesOption is a union discriminated on a literal type
function serieOption(): SeriesOption {
    const kleuren: string[] = puntKleuren(props.serie);
    const laatste: number   = props.serie.punten.length - 1;
    return {
        type: props.type,
        data: props.serie.punten.map((p, i) => ({
            value    : p.waarde,
            itemStyle: {color: props.type === 'bar' ? kleuren[i] : KLEUR_LIJN},
            label    : {show: !props.compact || i === laatste, position: 'top', formatter: () => format(p.waarde)},
        })),
        lineStyle: {color: KLEUR_LIJN},
        symbol   : 'circle',
        markLine : {silent: true, symbol: 'none', data: markLines()},
    } as unknown as SeriesOption;
}

const option = computed<EChartsOption>(() => ({
    grid   : {left: 8, right: 8, top: props.compact ? 24 : 32, bottom: props.compact ? 4 : 24, containLabel: !props.compact},
    tooltip: {trigger: 'axis', valueFormatter: (v: unknown) => format(Number(v))},
    xAxis  : {type: 'category', data: props.serie.punten.map(p => p.kwartaal), axisLabel: {show: !props.compact}},
    yAxis  : {
        type     : 'value',
        scale    : props.type === 'line',
        min      : props.type === 'bar' ? 0 : (props.type === 'line' ? yMin : undefined),
        max      : isPercentage.value ? 105 : (props.type === 'line' ? yMax : undefined),
        axisLabel: {show: !props.compact, formatter: isPercentage.value ? '{value}%' : undefined},
    },
    series: [serieOption()],
}));
</script>

<template>
    <v-chart :option="option" autoresize :style="{height: compact ? '180px' : '320px', width: '100%'}"/>
</template>
